import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeUserType from 'src/components/Badge/BadgeUserType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import displayUserName from 'src/util/i18n/decorators/displayUserName';

import AdminUserShowPropertyUser from './AdminUserShowPropertyUser';

/**
 * AdminUserShow component displays detailed information about a user
 * including their personal details and associated properties.
 * @param {object} props - Component props
 * @param {object} props.relay - Relay container props
 * @param {object} props.user - User data from GraphQL
 * @returns {React.ReactElement} - Rendered AdminUserShow component
 */
function AdminUserShow(props) {
  if (!props) {
    return <Loading />;
  }

  const { user } = props;
  const {
    uuid, email, givenName, familyName, externalIdentifier, type, active,
    createdAt, updatedAt, userProperties,
  } = user;
  const { start, finish } = active;

  const intl = useIntl();

  // Page title
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_user.admin_user_show.page_title', defaultMessage: 'Enosi Admin - User - {username}' },
    { username: displayUserName(intl, user) },
  );

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.admin.breadcrumb', defaultMessage: 'Admin' });
  const breadcrumbUsers = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.users.breadcrumb', defaultMessage: 'Users' });
  const breadcrumbUser = intl.formatMessage(
    { id: 'admin.admin_user.admin_user_show.user.breadcrumb', defaultMessage: 'User: {username}' },
    { username: displayUserName(intl, user) },
  );

  // Card headers
  const headerUserDetails = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.card.header.user_details', defaultMessage: 'User details' });
  const headerUserProperties = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.card.header.user_properties', defaultMessage: 'User properties' });

  // Table headers - User details
  const headerActiveStatus = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.active_status', defaultMessage: 'Active status' });
  const headerId = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.id', defaultMessage: 'ID' });
  const headerType = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.type', defaultMessage: 'Type' });
  const headerExtId = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.external_identifier', defaultMessage: 'Ext ID' });
  const headerExtIdTooltip = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.external_identifier.tooltip', defaultMessage: 'External identifier' });
  const headerEmail = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.email', defaultMessage: 'Email' });
  const headerGivenName = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.given_name', defaultMessage: 'Given name' });
  const headerFamilyName = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.family_name', defaultMessage: 'Family name' });
  const headerUnverifiedEmail = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.unverified_email', defaultMessage: 'Unverified email' });
  const headerUnverifiedEmailValue = intl.formatMessage({
    id: 'admin.admin_user.admin_user_show.table.value.unverified_email',
    defaultMessage: 'To be exposed',
  });
  const headerStart = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.start', defaultMessage: 'Start' });
  const headerFinish = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.finish', defaultMessage: 'Finish' });
  const headerCreatedAt = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.created_at', defaultMessage: 'Created at' });
  const headerUpdatedAt = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.table.header.updated_at', defaultMessage: 'Updated at' });

  // Table headers - User properties
  const headerPropertyUser = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property_user', defaultMessage: 'PropertyUser' });
  const headerProperty = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property', defaultMessage: 'Property' });
  const headerPropertyUserId = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.id', defaultMessage: 'ID' });
  const headerPropertyUserIdTooltip = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.id.tooltip', defaultMessage: 'Identifier' });
  const headerPropertyUserActive = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.active', defaultMessage: 'Active' });
  const headerPropertyUserExtId = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.external_identifier', defaultMessage: 'Ext ID' });
  const headerPropertyUserExtIdTooltip = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.external_identifier.tooltip', defaultMessage: 'External identifier' });
  const headerPropertyTitle = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.title', defaultMessage: 'Title' });
  const headerPropertyId = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property_id', defaultMessage: 'ID' });
  const headerPropertyIdTooltip = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property_id.tooltip', defaultMessage: 'Identifier' });
  const headerPropertyActive = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property_active', defaultMessage: 'Active' });
  const headerPropertyExtId = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property_external_identifier', defaultMessage: 'Ext ID' });
  const headerPropertyExtIdTooltip = intl.formatMessage({ id: 'admin.admin_user.admin_user_show.property_user.table.header.property_external_identifier.tooltip', defaultMessage: 'External identifier' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbUsers, path: '/admin/users' },
          { name: breadcrumbUser },
        ]}
      />

      <h1>
        {displayUserName(intl, user)}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {headerUserDetails}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{headerActiveStatus}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerId}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerType}</th>
                  <td>
                    <BadgeUserType type={type} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title={headerExtIdTooltip}>{headerExtId}</abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerEmail}</th>
                  <td>{email}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerGivenName}</th>
                  <td>{givenName}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerFamilyName}</th>
                  <td>{familyName}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerUnverifiedEmail}</th>
                  <td>{headerUnverifiedEmailValue}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerStart}</th>
                  <td>
                    {start && DateTime.fromSeconds(start).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerFinish}</th>
                  <td>
                    {finish && DateTime.fromSeconds(finish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerCreatedAt}</th>
                  <td>
                    {createdAt
                      && DateTime.fromSeconds(createdAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerUpdatedAt}</th>
                  <td>
                    {updatedAt
                      && DateTime.fromSeconds(updatedAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card>
            <CardHeader>
              {headerUserProperties}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col" colSpan={3} className="text-center">{headerPropertyUser}</th>
                  <th scope="col" colSpan={4} className="text-center">{headerProperty}</th>
                </tr>
                <tr>
                  <th scope="col">
                    <abbr title={headerPropertyUserIdTooltip}>{headerPropertyUserId}</abbr>
                  </th>
                  <th scope="col">{headerPropertyUserActive}</th>
                  <th scope="col">
                    <abbr title={headerPropertyUserExtIdTooltip}>{headerPropertyUserExtId}</abbr>
                  </th>
                  <th scope="col">{headerPropertyTitle}</th>
                  <th scope="col">
                    <abbr title={headerPropertyIdTooltip}>{headerPropertyId}</abbr>
                  </th>
                  <th scope="col">{headerPropertyActive}</th>
                  <th scope="col">
                    <abbr title={headerPropertyExtIdTooltip}>{headerPropertyExtId}</abbr>
                  </th>
                </tr>
              </thead>
              <tbody>
                {userProperties && userProperties.edges.map((el) => (
                  <AdminUserShowPropertyUser propertyUser={el.node} key={el.node?.uuid} />
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminUserShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  user: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminUserShow.defaultProps = {
  user: null,
};

export default createFragmentContainer(
  AdminUserShow,
  {
    user: graphql`
      fragment AdminUserShow_user on User {
        uuid
        externalIdentifier
        type
        email
        givenName
        familyName
        active { start finish }
        createdAt
        updatedAt
        userProperties(first: 500) {
          edges {
            node {
              uuid
              externalIdentifier
              active { start finish }
              property { uuid externalIdentifier title active { start finish } }
            }
          }
        }
      }
    `,
  },
);
